import * as mobx from 'mobx';
import { IntlShape } from 'react-intl';
import * as IoC from 'typescript-ioc';
import { JobType } from '../../../../../api/jobs.dao';
import { IUserOperatorDTO, OperatorRole } from '../../../../../api/operators.dao';
import { IUsersDAO } from '../../../../../api/users.dao';
import { JobDtoType, LongRunningJob } from '../../../../../domain/jobs';
import { EventExecutionRuntime } from '../../../../../domain/jobs/implementations/event-execution-runtime';
import { IErrorParsing } from '../../../../../infrastructure/error-parsing';
import { alertServerError } from '../../../../global/modal/alerts-host/alert-templates';
import { IConnectionsDAO } from '../../../../../api/connections.dao';
import { IEventRequestParams } from '../../../../../api/shared.dto';

class OperatorsBulkUpdateFormViewModel {
    @mobx.observable
    public operatorList: IUserOperatorDTO[] | null = null;
    @mobx.observable
    private operatorsListToAdd: IUserOperatorDTO[] = [];
    @mobx.observable
    private meetingsOperators: IUserOperatorDTO[] = [];
    @mobx.observable
    private operatorListToRemove: Map<string, IUserOperatorDTO> = new Map();
    @mobx.observable
    private meetingsOperatorsQuery: string = '';

    public static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }

    constructor(
        public intl: IntlShape,
        private selectedMeetingsIds: number[],
        private eventRequestParams: IEventRequestParams,
        private saveBulkCompletedCallback?: (attendeesToUpdate: JobDtoType) => void,
        @IoC.Inject private readonly _usersDAO: IUsersDAO = IoC.Container.get(IUsersDAO),
        @IoC.Inject private readonly connectionsDAO: IConnectionsDAO = IoC.Container.get(IConnectionsDAO),
        @IoC.Inject private readonly _jobsPipeline = IoC.Container.get(EventExecutionRuntime)
    ) {
        this._jobsPipeline.onComplete(JobType.MeetingOperatorsUpdateBulk, () => {
            if (this.saveBulkCompletedCallback) this.saveBulkCompletedCallback.bind(this);
        });
    }

    public get operators() { return this.operatorList; }
    public get operatorsToAdd() { return this.operatorsListToAdd; }
    private get bulkUpdateMeetingConnectionsJob(): LongRunningJob {
        return this._jobsPipeline.getJobByType(JobType.MeetingOperatorsUpdateBulk);
    }

    @mobx.action
    public initOperatorsData = async (query: string = ''): Promise<void> => {
        try {
            const activeOnlyUsers = { activeOnly: true };
            const { users = [] } = await this._usersDAO.searchDeliveryTeamMembers(query ? {query: query, ...activeOnlyUsers } : { ...activeOnlyUsers } );
            mobx.runInAction(() => {
                this.operatorList = users.map(user =>
                    ({
                        fullName: `${user.firstName} ${user.lastName}`,
                        firstName: user.firstName,
                        lastName: user.lastName,
                        email: user.email,
                        user: user
                    }));
            });
        } catch (e) {
            OperatorsBulkUpdateFormViewModel.handleApiException(e);
        }
    };

    @mobx.action.bound
    public async initMeetingsOperatorsData(): Promise<void> {
        try {
            const params = { ...this.eventRequestParams, meetingIds: this.selectedMeetingsIds.join() };
            const { meetingOperators = [] } = await this.connectionsDAO.getConnectionsOperatorsByMeetingIds(params);

            mobx.runInAction(() => {
                this.meetingsOperators = meetingOperators.map(operator =>
                    ({
                        fullName: `${operator.firstName} ${operator.lastName}`,
                        user: operator.user,
                        ...operator
                    }));
            });
        } catch (e) {
            OperatorsBulkUpdateFormViewModel.handleApiException(e);
        }
    }

    public get meetingsAssignedOperators(): IUserOperatorDTO[] {
        return this.getFilteredMeetingsOperators();
    }

    public get operatorsToRemove(): IUserOperatorDTO[] {
        return [ ...this.operatorListToRemove.values() ];
    }

    @mobx.action.bound
    public setOperatorsToRemove(operators: IUserOperatorDTO[]): void {
        // eslint-disable-next-line no-unused-vars
        operators.forEach(({ fullName, ...operator }) => this.operatorListToRemove.set(operator.email, operator));
    }

    @mobx.action.bound
    public removeOperatorFromRemoveList(email: string): void {
        this.operatorListToRemove.delete(email);
    }

    @mobx.action.bound
    public searchOperatorsInMeetings(query: string = ''): void {
        this.meetingsOperatorsQuery = query || '';
    }

    private getFilteredMeetingsOperators(): IUserOperatorDTO[] {
        const query = this.meetingsOperatorsQuery.toLowerCase().trim();

        return query
            ? this.meetingsOperators.filter(operator => operator.fullName.toLowerCase().includes(query))
            : this.meetingsOperators
        ;
    }

    @mobx.action.bound
    public setOperators(operatorList: IUserOperatorDTO[]): void {
        // eslint-disable-next-line no-unused-vars
        this.operatorsListToAdd = operatorList.map(({ fullName, ...rest }) => rest);
    }

    @mobx.action.bound
    public onOperatorRoleChange(email: string, role: OperatorRole): void {
        const operators = [...this.operatorsListToAdd];

        const currentOperatorIndex = operators.findIndex(item => item.email === email);
        operators[currentOperatorIndex].operatorRole =
            role === operators[currentOperatorIndex].operatorRole ? null : role;
        this.operatorsListToAdd = operators;

    }

    @mobx.action.bound
    public removeOperatorByEmail(email: string): void {
        this.operatorsListToAdd = this.operatorsListToAdd.filter(o => o.email !== email);
    }

    public async bulkUpdateOperatorsApiCall(): Promise<boolean> {
        try {
            if (!this.operatorsListToAdd.length && !this.operatorsToRemove.length) return false;
            const job = this.bulkUpdateMeetingConnectionsJob;

            if (this.saveBulkCompletedCallback) job.onComplete(this.saveBulkCompletedCallback.bind(this));

            const description = this.intl.formatMessage({
                id: 'oe.job.meetingsOperatorsUpdateInProgress',
                defaultMessage: 'Meeting Operators update is in progress',
                description: 'Job description title'
            });

            await job.trigger({
                bulkAddedOperators: this.operatorsListToAdd,
                bulkRemovedOperators: this.operatorsToRemove,
                meetingIds: this.selectedMeetingsIds,
            }, { description: description });
            return true;

        } catch (error) {
            return false;
        }
    }
}

export { OperatorsBulkUpdateFormViewModel };
